import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    GetInvoices,
    GetInvoiceById,
    GetInvoiceStats,
    GetInvoiceSettings,
    UpdateInvoiceSettings,
    GenerateInvoices,
    UpdateInvoiceStatus,
    AddInvoicePayment,
    CustomerMarkInvoicePaid,
    ApproveInvoicePayment,
    RejectInvoicePayment,
    SyncInvoice,
    UpdateInvoiceDueDate,
    UpdateInvoice,
    UpdateInvoicePayment,
    DeleteInvoicePayment,
    CreateManualInvoice,
} from '../services/backend';
import { NotifyError } from '../utils/functions';

const INVOICES_KEY = 'invoices';
const INVOICE_STATS_KEY = 'invoice-stats';
const INVOICE_SETTINGS_KEY = 'invoice-settings';

export function useGetInvoices(params = {}) {
    return useQuery([INVOICES_KEY, params], () => GetInvoices(params), {
        staleTime: 0,
        cacheTime: 0,
        select: (data) => data?.data,
        onError: (error) => {
            NotifyError("Failed to fetch invoices", error);
            console.error("Error fetching invoices:", error);
        }
    });
}

export function useGetInvoiceById(invoiceId) {
    return useQuery([INVOICES_KEY, invoiceId], () => GetInvoiceById(invoiceId), {
        enabled: !!invoiceId,
        staleTime: 0,
        cacheTime: 0,
        select: (data) => data?.data?.invoice,
        onError: (error) => {
            NotifyError("Failed to fetch invoice", error);
            console.error("Error fetching invoice:", error);
        }
    });
}

export function useGetInvoiceStats() {
    return useQuery(INVOICE_STATS_KEY, GetInvoiceStats, {
        staleTime: 0,
        cacheTime: 0,
        select: (data) => data?.data?.stats,
        onError: (error) => {
            NotifyError("Failed to fetch invoice stats", error);
            console.error("Error fetching invoice stats:", error);
        }
    });
}

export function useGetInvoiceSettings() {
    return useQuery(INVOICE_SETTINGS_KEY, GetInvoiceSettings, {
        staleTime: 60000,
        select: (data) => data?.data?.settings,
        onError: (error) => {
            NotifyError("Failed to fetch invoice settings", error);
        }
    });
}

export function useUpdateInvoiceSettings() {
    const queryClient = useQueryClient();
    return useMutation(UpdateInvoiceSettings, {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICE_SETTINGS_KEY);
        },
    });
}

export function useGenerateInvoices() {
    const queryClient = useQueryClient();
    return useMutation(GenerateInvoices, {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}

export function useUpdateInvoiceStatus() {
    const queryClient = useQueryClient();
    return useMutation(({ invoiceId, data }) => UpdateInvoiceStatus(invoiceId, data), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}

export function useAddInvoicePayment() {
    const queryClient = useQueryClient();
    return useMutation(({ invoiceId, data }) => AddInvoicePayment(invoiceId, data), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}

export function useCustomerMarkInvoicePaid() {
    const queryClient = useQueryClient();
    return useMutation(({ invoiceId, data }) => CustomerMarkInvoicePaid(invoiceId, data), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
        },
    });
}

export function useApproveInvoicePayment() {
    const queryClient = useQueryClient();
    return useMutation(({ invoiceId, paymentIndex }) => ApproveInvoicePayment(invoiceId, paymentIndex), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}

export function useRejectInvoicePayment() {
    const queryClient = useQueryClient();
    return useMutation(({ invoiceId, paymentIndex }) => RejectInvoicePayment(invoiceId, paymentIndex), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}
export function useSyncInvoice() {
    const queryClient = useQueryClient();
    return useMutation((invoiceId) => SyncInvoice(invoiceId), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}

export function useUpdateInvoiceDueDate() {
    const queryClient = useQueryClient();
    return useMutation(({ invoiceId, data }) => UpdateInvoiceDueDate(invoiceId, data), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
        },
    });
}

export function useUpdateInvoice() {
    const queryClient = useQueryClient();
    return useMutation(({ invoiceId, data }) => UpdateInvoice(invoiceId, data), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}

export function useUpdatePayment() {
    const queryClient = useQueryClient();
    return useMutation(({ invoiceId, paymentIndex, data }) => UpdateInvoicePayment(invoiceId, paymentIndex, data), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}

export function useDeletePayment() {
    const queryClient = useQueryClient();
    return useMutation(({ invoiceId, paymentIndex }) => DeleteInvoicePayment(invoiceId, paymentIndex), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}

export function useCreateManualInvoice() {
    const queryClient = useQueryClient();
    return useMutation((data) => CreateManualInvoice(data), {
        onSuccess: () => {
            queryClient.invalidateQueries(INVOICES_KEY);
            queryClient.invalidateQueries(INVOICE_STATS_KEY);
        },
    });
}
